import { useAuthenticator } from "@aws-amplify/ui-react";
import { SpinnerOverlay } from "components/SpinnerOverlay";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useCallback, useEffect } from "react";
import {
  matchPath,
  Outlet,
  ScrollRestoration,
  useLocation,
  useMatch,
  useNavigate,
} from "react-router-dom";

/**
 * Customer routes that intentionally live outside `/o/:tenantId`. Most customer
 * pages need org context in the URL; these are user-centric instead (see
 * CommandPaletteDefaults notifications comment).
 */
export const CUSTOMER_GLOBAL_PATH_PREFIXES = ["/notifications"] as const;

export function isCustomerGlobalPath(pathname: string): boolean {
  return CUSTOMER_GLOBAL_PATH_PREFIXES.some(
    (prefix) => pathname === prefix || pathname.startsWith(`${prefix}/`)
  );
}

export function isCustomerTenantPath(pathname: string): boolean {
  return Boolean(matchPath({ path: "/o/:tenantId/*", end: false }, pathname));
}

export function isCustomerAllowedPath(pathname: string): boolean {
  return isCustomerTenantPath(pathname) || isCustomerGlobalPath(pathname);
}

export function PrivateRoute() {
  const { authStatus, toSignIn } = useAuthenticator();
  const navigate = useNavigate();
  const location = useLocation();
  const { isStaff, isCustomer, user } = useCurrentUser();
  const match = useMatch("/");
  const onAllowedCustomerPath = isCustomerAllowedPath(location.pathname);

  const navigateToOrganizationHome = useCallback(() => {
    const tenantId = user?.UserTenant[0]?.tenantId;
    if (!tenantId) return;
    navigate(`/o/${tenantId}`);
  }, [user?.UserTenant, navigate]);

  useEffect(() => {
    if (authStatus === "authenticated" && isCustomer && !onAllowedCustomerPath) {
      navigateToOrganizationHome();
    }
  }, [authStatus, isCustomer, onAllowedCustomerPath, navigateToOrganizationHome]);

  useEffect(() => {
    if (!match) return;
    if (!(isStaff || isCustomer)) return;

    if (authStatus !== "authenticated" && authStatus !== "configuring") {
      if (!localStorage.getItem("logout")) {
        toSignIn({ scope: "email openid phone" });
      } else {
        localStorage.removeItem("logout");
      }
    }

    if (authStatus === "authenticated") {
      const preLoginPath = localStorage.getItem("preLoginPath");
      localStorage.removeItem("preLoginPath");
      if (preLoginPath) {
        if (isCustomer && !isCustomerAllowedPath(preLoginPath)) {
          navigateToOrganizationHome();
        } else {
          navigate(preLoginPath);
        }
      } else {
        if (isStaff) navigate("/staff");
        else if (isCustomer && user?.UserTenant[0]?.tenantId) {
          navigateToOrganizationHome();
        }
      }
    }
  }, [
    authStatus,
    toSignIn,
    navigate,
    isStaff,
    isCustomer,
    user?.UserTenant,
    match,
    navigateToOrganizationHome,
  ]);

  if (authStatus === "configuring") {
    return <div>Signing you in/out</div>;
  }

  return authStatus !== "authenticated" ||
    (authStatus === "authenticated" && !isStaff && !isCustomer) ? (
    <SpinnerOverlay loading />
  ) : (
    <>
      <Outlet />
      <ScrollRestoration />
    </>
  );
}
